import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { playAddToCart } from '../utils/sound';

const API = `${window.location.origin}/api`;
const CartContext = createContext();

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('jsf_cart');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem('jsf_cart', JSON.stringify(cart));
  }, [cart]);

  // Sync cart snapshot to backend (debounced, for abandoned cart recovery emails)
  useEffect(() => {
    const email = user && user !== false ? user.email : null;
    if (!email || cart.length === 0) return;

    const t = setTimeout(() => {
      const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
      axios.post(`${API}/cart/snapshot`, {
        customer_email: email,
        customer_name: user.name || '',
        items: cart.map(i => ({
          product_id: i.product_id,
          product_name: i.product_name,
          variant_name: i.variant ? `${i.variant.size} ${i.variant.type}` : '',
          price: i.price,
          quantity: i.quantity,
        })),
        subtotal,
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [cart, user]);

  const addToCart = useCallback((product, quantity = 1, variant = null) => {
    playAddToCart();
    setCart(prev => {
      const key = variant
        ? `${product.id}-${variant.size}-${variant.type}`
        : product.id;
      const existing = prev.find(item => item.cartKey === key);
      if (existing) {
        return prev.map(item =>
          item.cartKey === key
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, {
        cartKey: key,
        product_id: product.id,
        product_name: product.name,
        image_url: product.image_url,
        price: variant ? variant.price : product.base_price,
        quantity,
        variant: variant ? { size: variant.size, type: variant.type } : null,
      }];
    });
  }, []);

  const updateQuantity = useCallback((cartKey, quantity) => {
    if (quantity <= 0) {
      setCart(prev => prev.filter(item => item.cartKey !== cartKey));
    } else {
      setCart(prev => prev.map(item =>
        item.cartKey === cartKey ? { ...item, quantity } : item
      ));
    }
  }, []);

  const removeFromCart = useCallback((cartKey) => {
    setCart(prev => prev.filter(item => item.cartKey !== cartKey));
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
    // Also clear server-side snapshot so no recovery email goes out
    const email = user && user !== false ? user.email : null;
    if (email) {
      axios.delete(`${API}/cart/snapshot`, { data: { customer_email: email } }).catch(() => {});
    }
  }, [user]);

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, updateQuantity, removeFromCart, clearCart, cartCount, cartTotal }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
